import React from 'react';
import SectionIntro from '../primitives/SectionIntro';
import HelperNote from '../primitives/HelperNote';
import TextField from '../primitives/TextField';
import CurrencyField from '../primitives/CurrencyField';
import PillGroup from '../primitives/PillGroup';
import DateTriplet from '../primitives/DateTriplet';
import RepeaterCard from '../primitives/RepeaterCard';
import ApplicantOwnership from '../primitives/ApplicantOwnership';
import CategoryCard, { ICONS } from '../primitives/CategoryCard';

const LEASE_TYPE_OPTIONS = ['Vehicle', 'Equipment', 'Novated', 'Other'];

// Stable ids so cross-section links (e.g. property -> loan) survive
// add/remove churn. Loans/cards are stored in a map keyed by these ids.
const makeStableId = (prefix) => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    try {
      return `${prefix}_${window.crypto.randomUUID()}`;
    } catch (e) {
      // fall through
    }
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

class StepLiabilities extends React.Component {
  buildLoan = () => {
    const id = makeStableId('loan');
    return {
      id,
      bank_name: '',
      owing: '',
      interest_rate: '',
      repayment: '',
      held_other_text: '',
      ownership: {},
    };
  };

  buildCard = () => {
    const id = makeStableId('card');
    return {
      id,
      bank_name: '',
      owing: '',
      limit: '',
      held_other_text: '',
      ownership: {},
    };
  };

  buildLease = () => {
    const id = makeStableId('lease');
    return {
      id,
      lessor: '',
      lease_type: '',
      monthly_payment: '',
      end_date: { day: '', month: '', year: '' },
      held_other_text: '',
      ownership: {},
    };
  };

  addLoan = () => {
    const next = { ...(this.props.liabilities.loans || {}) };
    const loan = this.buildLoan();
    next[loan.id] = loan;
    this.props.onUpdateLiabilities({ loans: next });
  };

  removeLoan = (key) => {
    // Delegate to the parent so cross-section references (property
    // linked_loan_ids etc.) can be cleaned up in the same setState.
    if (this.props.onRemoveLoan) {
      this.props.onRemoveLoan(key);
      return;
    }
    const next = { ...(this.props.liabilities.loans || {}) };
    delete next[key];
    this.props.onUpdateLiabilities({ loans: next });
  };

  patchLoan = (key, field, value) => {
    const loans = { ...(this.props.liabilities.loans || {}) };
    loans[key] = { ...(loans[key] || {}), [field]: value };
    this.props.onUpdateLiabilities({ loans });
  };

  addCard = () => {
    const next = { ...(this.props.liabilities.credit_cards || {}) };
    const card = this.buildCard();
    next[card.id] = card;
    this.props.onUpdateLiabilities({ credit_cards: next });
  };

  removeCard = (key) => {
    const next = { ...(this.props.liabilities.credit_cards || {}) };
    delete next[key];
    this.props.onUpdateLiabilities({ credit_cards: next });
  };

  patchCard = (key, field, value) => {
    const cards = { ...(this.props.liabilities.credit_cards || {}) };
    cards[key] = { ...(cards[key] || {}), [field]: value };
    this.props.onUpdateLiabilities({ credit_cards: cards });
  };

  addLease = () => {
    const next = { ...(this.props.liabilities.leases || {}) };
    const lease = this.buildLease();
    next[lease.id] = lease;
    this.props.onUpdateLiabilities({ leases: next });
  };

  removeLease = (key) => {
    const next = { ...(this.props.liabilities.leases || {}) };
    delete next[key];
    this.props.onUpdateLiabilities({ leases: next });
  };

  patchLease = (key, field, value) => {
    const leases = { ...(this.props.liabilities.leases || {}) };
    leases[key] = { ...(leases[key] || {}), [field]: value };
    this.props.onUpdateLiabilities({ leases });
  };

  // Look up which property (if any) has linked this loan, so we can show a
  // small "Secured by ..." hint on the loan card.
  loanLinkedPropertyLabel = (loanId) => {
    const props = (this.props.properties || {});
    const match = Object.values(props).find((p) =>
      Array.isArray(p.linked_loan_ids) && p.linked_loan_ids.includes(loanId)
    );
    if (!match) return null;
    return match.address || 'a property';
  };

  render() {
    const { liabilities, applicants } = this.props;
    const loans = liabilities.loans || {};
    const cards = liabilities.credit_cards || {};
    const leases = liabilities.leases || {};
    const loanKeys = Object.keys(loans);
    const cardKeys = Object.keys(cards);
    const leaseKeys = Object.keys(leases);

    return (
      <div className="space-y-5">
        {/* Intro */}
        <div className="rounded-2xl bg-white shadow-sm border border-[#E5E7EB] p-5 sm:p-8">
          <SectionIntro
            eyebrow="About your finances - Liabilities"
            title="Loans, credit cards & other debts"
            description="Let's get a picture of what you currently owe. Please include all your debts - it helps us assess your loan serviceability."
          />
          <HelperNote>
            <strong>Heads up:</strong> Include any home loans / mortgages here too. We'll capture
            the property details in the Assets section.
          </HelperNote>
        </div>

        {/* Loans */}
        <CategoryCard
          icon={ICONS.bank}
          title="Loans"
          description="Home loans, personal loans, car loans, HECS, BNPL plans, etc."
          count={loanKeys.length}
        >
          {loanKeys.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-5 text-center">
              <p className="text-sm text-[#6B7280]">No loans added.</p>
              <p className="text-xs text-[#9CA3AF] mt-1">Home loans, personal loans, car loans, HECS, BNPL plans, etc.</p>
            </div>
          ) : null}

          {loanKeys.map((key, i) => {
            const loan = loans[key];
            const linkedProperty = this.loanLinkedPropertyLabel(loan.id);
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Loan ${i + 1}`}
                subtitle={loan.bank_name || ''}
                badge={linkedProperty ? 'Linked to a property' : undefined}
                onRemove={() => this.removeLoan(key)}
              >
                <TextField
                  label="Lender / bank"
                  required
                  value={loan.bank_name}
                  onChange={(v) => this.patchLoan(key, 'bank_name', v)}
                  fieldId={`liabilities.loans.${loan.id}.bank_name`}
                  error={errs[`liabilities.loans.${loan.id}.bank_name`]}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CurrencyField
                    label="Amount owing"
                    required
                    value={loan.owing}
                    onChange={(v) => this.patchLoan(key, 'owing', v)}
                    fieldId={`liabilities.loans.${loan.id}.owing`}
                    error={errs[`liabilities.loans.${loan.id}.owing`]}
                  />
                  <CurrencyField
                    label="Interest rate"
                    prefix=""
                    suffix="% p.a."
                    value={loan.interest_rate}
                    onChange={(v) => this.patchLoan(key, 'interest_rate', v)}
                  />
                  <CurrencyField
                    label="Monthly repayment"
                    required
                    value={loan.repayment}
                    onChange={(v) => this.patchLoan(key, 'repayment', v)}
                    suffix="/month"
                    fieldId={`liabilities.loans.${loan.id}.repayment`}
                    error={errs[`liabilities.loans.${loan.id}.repayment`]}
                  />
                </div>
                <ApplicantOwnership
                  label="Who is this loan in the name of?"
                  required
                  applicants={applicants}
                  value={loan.ownership}
                  onChange={(v) => this.patchLoan(key, 'ownership', v)}
                  fieldId={`liabilities.loans.${loan.id}.ownership`}
                  error={errs[`liabilities.loans.${loan.id}.ownership`]}
                />
                <TextField
                  label="Other (optional)"
                  placeholder="e.g. company, guarantor 50%, joint with parent"
                  value={loan.held_other_text}
                  onChange={(v) => this.patchLoan(key, 'held_other_text', v)}
                  hint="Use this if the loan isn't simply in an applicant's name."
                />
              </RepeaterCard>
            );
          })}

          <button
            type="button"
            onClick={this.addLoan}
            className="w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold"
            style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
          >
            + Add a loan
          </button>
        </CategoryCard>

        {/* Credit cards */}
        <CategoryCard
          icon={ICONS.creditCard}
          title="Credit cards"
          description="Include retail cards, charge cards and store cards too."
          count={cardKeys.length}
        >
          {cardKeys.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-5 text-center">
              <p className="text-sm text-[#6B7280]">No credit cards added.</p>
              <p className="text-xs text-[#9CA3AF] mt-1">Include retail cards, charge cards and store cards too.</p>
            </div>
          ) : null}

          {cardKeys.map((key, i) => {
            const card = cards[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Credit card ${i + 1}`}
                subtitle={card.bank_name || ''}
                onRemove={() => this.removeCard(key)}
              >
                <TextField
                  label="Provider / bank"
                  required
                  value={card.bank_name}
                  onChange={(v) => this.patchCard(key, 'bank_name', v)}
                  fieldId={`liabilities.credit_cards.${card.id}.bank_name`}
                  error={errs[`liabilities.credit_cards.${card.id}.bank_name`]}
                />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CurrencyField
                    label="Amount owing"
                    value={card.owing}
                    onChange={(v) => this.patchCard(key, 'owing', v)}
                  />
                  <CurrencyField
                    label="Credit limit"
                    required
                    value={card.limit}
                    onChange={(v) => this.patchCard(key, 'limit', v)}
                    fieldId={`liabilities.credit_cards.${card.id}.limit`}
                    error={errs[`liabilities.credit_cards.${card.id}.limit`]}
                  />
                </div>
                <ApplicantOwnership
                  label="Whose card is this?"
                  required
                  applicants={applicants}
                  value={card.ownership}
                  onChange={(v) => this.patchCard(key, 'ownership', v)}
                  fieldId={`liabilities.credit_cards.${card.id}.ownership`}
                  error={errs[`liabilities.credit_cards.${card.id}.ownership`]}
                />
                <TextField
                  label="Other (optional)"
                  placeholder="e.g. company card, supplementary cardholder"
                  value={card.held_other_text}
                  onChange={(v) => this.patchCard(key, 'held_other_text', v)}
                />
              </RepeaterCard>
            );
          })}

          <button
            type="button"
            onClick={this.addCard}
            className="w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold"
            style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
          >
            + Add a credit card
          </button>
        </CategoryCard>

        {/* Leases */}
        <CategoryCard
          icon={ICONS.refresh}
          title="Leases"
          description="Novated, vehicle, equipment or other ongoing leases - different to personal loans."
          count={leaseKeys.length}
        >
          {leaseKeys.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] p-5 text-center">
              <p className="text-sm text-[#6B7280]">No leases added.</p>
              <p className="text-xs text-[#9CA3AF] mt-1">Novated, vehicle, equipment or other ongoing leases.</p>
            </div>
          ) : null}

          {leaseKeys.map((key, i) => {
            const lease = leases[key];
            const errs = this.props.errors || {};
            return (
              <RepeaterCard
                key={key}
                title={`Lease ${i + 1}`}
                subtitle={lease.lessor || ''}
                onRemove={() => this.removeLease(key)}
              >
                <TextField
                  label="Lessor / provider"
                  required
                  value={lease.lessor}
                  onChange={(v) => this.patchLease(key, 'lessor', v)}
                  fieldId={`liabilities.leases.${lease.id}.lessor`}
                  error={errs[`liabilities.leases.${lease.id}.lessor`]}
                />
                <div>
                  <label className="block text-sm font-medium text-[#1F2937] mb-2">
                    Lease type <span className="text-[#DC2626]">*</span>
                  </label>
                  <PillGroup
                    columns={4}
                    options={LEASE_TYPE_OPTIONS}
                    value={lease.lease_type}
                    onChange={(v) => this.patchLease(key, 'lease_type', v)}
                    fieldId={`liabilities.leases.${lease.id}.lease_type`}
                    error={errs[`liabilities.leases.${lease.id}.lease_type`]}
                  />
                </div>
                <CurrencyField
                  label="Monthly payment"
                  required
                  value={lease.monthly_payment}
                  onChange={(v) => this.patchLease(key, 'monthly_payment', v)}
                  suffix="/month"
                  fieldId={`liabilities.leases.${lease.id}.monthly_payment`}
                  error={errs[`liabilities.leases.${lease.id}.monthly_payment`]}
                />
                <DateTriplet
                  label="Lease end date"
                  value={lease.end_date}
                  onChange={(v) => this.patchLease(key, 'end_date', v)}
                />
                <ApplicantOwnership
                  label="Lease in the name of"
                  required
                  applicants={applicants}
                  value={lease.ownership}
                  onChange={(v) => this.patchLease(key, 'ownership', v)}
                  fieldId={`liabilities.leases.${lease.id}.ownership`}
                  error={errs[`liabilities.leases.${lease.id}.ownership`]}
                />
                <TextField
                  label="Other (optional)"
                  placeholder="e.g. company, guarantor, joint with parent"
                  value={lease.held_other_text}
                  onChange={(v) => this.patchLease(key, 'held_other_text', v)}
                />
              </RepeaterCard>
            );
          })}

          <button
            type="button"
            onClick={this.addLease}
            className="w-full rounded-xl border-2 border-dashed py-3 text-sm font-semibold"
            style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
          >
            + Add a lease
          </button>
        </CategoryCard>
      </div>
    );
  }
}

export default StepLiabilities;
